import { EErrorCode } from 'config/errors-codes';
import type { IBrand, IMarket } from 'data/schema';
import { consumeDealerIdFromUrl } from 'utils/auth/applyDealerIdFromUrl';
import {
  activateDealerTenant,
  getDealerContextPath,
  isDealerGatePassed,
  markDealerGatePassed,
  readSessionDealerHint,
} from 'utils/auth/dealerGate';
import { getDealerIdsToRuleOut, getUnavailableReason } from 'utils/auth/dealerIdOptions';
import { resolveDealerChoice, resolveDealerForLogin, resolveDealerWindow } from 'utils/auth/dealerResolution';
import type {
  IDealerIdOption,
  IDealerSelectionError,
  TDealerWindowResolution,
} from 'utils/auth/dealerResolution.types';
import {
  commitDealerSelection,
  markDealerIdsUnavailable,
  readLastConfirmedDealerId,
  readUnavailableDealerIds,
  rememberConfirmedDealerId,
} from 'utils/auth/dealerSelection';
import logger from 'utils/log';
import type { ICustomerFacingErrorOptions } from 'utils/ui/errors';

/**
 * The standalone dealer gate (AVS-4405) as a state machine with no React in it.
 * Standalone only: the only importer is `router/SecureOutlet.tsx`; web components never run this gate.
 * Every read of the user, the URL query and the dealer whitelist, and every effect, goes through `IGateRun`.
 */

/** What the gate view shows: nothing while the gate works, the dealer dialog, or the routed children. */
export type TGateView =
  | { status: 'pending' }
  | { status: 'open' }
  | {
      status: 'choose';
      brand: IBrand;
      market: IMarket;
      options: IDealerIdOption[];
      preselectedDealerId?: string;
      error?: IDealerSelectionError;
      onSelect: (dealerId: string) => void;
    };

export const PENDING: TGateView = { status: 'pending' };
export const OPEN: TGateView = { status: 'open' };

/** One run of the gate for one brand and market. Every effect of a run goes through it, so a stale run is inert. */
export interface IGateRun {
  brand: IBrand;
  market: IMarket;
  sessionId?: string;
  dealerIds: readonly string[];
  /** Only a dealer user picks a Dealer ID; anyone else opens the gate with an empty one. */
  isDealer: boolean;
  /** The Dealer ID committed earlier in this login, if any. */
  readStoredDealerId: () => string | undefined;
  /** The current URL query string (with its leading `?`), forwarded when the dealer switches brand or market. */
  readSearch: () => string;
  /** Checks the dealer against the backend whitelist. */
  validateAccess: (brand: string, market: string, dealerId: string) => Promise<{ allowed: boolean; code?: EErrorCode }>;
  isCancelled: () => boolean;
  show: (view: TGateView) => void;
  fail: (code: EErrorCode, options?: ICustomerFacingErrorOptions) => void;
  /** A React Router navigation (no document load), e.g. the brand or market switch; ignored once the run is cancelled. */
  navigate: (path: string) => void;
  revalidate: () => Promise<unknown>;
}

/** Any unexpected failure ends in the same error screen the gate always used for it. */
async function guarded(run: IGateRun, step: () => Promise<void>): Promise<void> {
  try {
    await step();
  } catch {
    run.fail(EErrorCode.DISABLED_MARKET);
  }
}

function showDialog(
  run: IGateRun,
  options: readonly IDealerIdOption[],
  error?: IDealerSelectionError,
  preselectedDealerId?: string
): void {
  run.show({
    status: 'choose',
    brand: run.brand,
    market: run.market,
    options: [...options],
    preselectedDealerId,
    error,
    onSelect: (dealerId) => chooseDealer(run, dealerId),
  });
}

/**
 * Handles the outcome of re-resolving the window after a commit (a rejected choice, or a recovery):
 * `selected` auto-starts the only remaining fit, `choose` reopens the window. `overrideError` lets a
 * recovery show its own reason (the just-failed Dealer ID) instead of the resolver's.
 */
function showResolution(
  run: IGateRun,
  resolution: TDealerWindowResolution,
  overrideError?: IDealerSelectionError
): void {
  if (resolution.status === 'selected') {
    void guarded(run, () => commitAndOpen(run, resolution.dealerId));
    return;
  }

  showDialog(run, resolution.options, overrideError ?? resolution.error, resolution.preselectedDealerId);
}

/** Re-resolves the window's options with no link, stored choice or hint: used after a rejected choice or a recovery. */
function resolveWindowAfterCommit(run: IGateRun, unavailable: ReturnType<typeof readUnavailableDealerIds>) {
  const { dealerIds, brand, market } = run;

  return resolveDealerWindow({
    dealerIds,
    brand,
    market,
    unavailable,
    lastConfirmedDealerId: readLastConfirmedDealerId(),
  });
}

/**
 * Stores the choice for this login and opens the gate.
 * Every caller passes an ID already validated against this brand and market (`resolveDealerForLogin`,
 * `resolveDealerWindow` and `resolveDealerChoice` only return fitting IDs), so the commit's own
 * validation cannot reject it and its result is not used. It stays as a guard on what is stored.
 */
function commitSelection(dealerId: string, context: { dealerIds: readonly string[]; brand: IBrand; market: IMarket }) {
  const committed = commitDealerSelection(dealerId, context);

  if (!committed.ok && import.meta.env.DEV) {
    logger.warn('Dealer gate: a validated Dealer ID was rejected on commit', { dealerId });
  }
}

async function commitAndOpen(run: IGateRun, dealerId: string): Promise<void> {
  const { brand, market, dealerIds } = run;

  commitSelection(dealerId, { dealerIds, brand, market });

  run.show(PENDING);
  await openGate(run, dealerId);
}

/** Pressing OK on a Dealer ID in the window: reject with a reason, stay, or switch brand and market. */
function chooseDealer(run: IGateRun, dealerId: string): void {
  const { dealerIds, brand, market } = run;
  const unavailable = readUnavailableDealerIds();
  const choice = resolveDealerChoice(dealerId, { dealerIds, brand, market, unavailable });

  if (choice.status === 'rejected') {
    showResolution(run, resolveWindowAfterCommit(run, unavailable), choice.error);
    return;
  }

  commitSelection(dealerId, { dealerIds, brand: choice.brand, market: choice.market });

  run.show(PENDING);

  if (choice.switchesContext) {
    run.navigate(getDealerContextPath(choice.brand, choice.market, run.readSearch()));
    return;
  }

  void guarded(run, () => openGate(run, dealerId));
}

/**
 * A tenant or whitelist failure: rules out the failed Dealer ID (or its whole brand and market for a
 * disabled market), then reopens the window with every remaining option, greyed out when nothing is
 * left. It never throws for the failure itself.
 */
async function recoverFromGateFailure(run: IGateRun, dealerId: string, code: EErrorCode): Promise<void> {
  markDealerIdsUnavailable(getDealerIdsToRuleOut(run.dealerIds, dealerId, code), code);

  const resolution = resolveWindowAfterCommit(run, readUnavailableDealerIds());
  showResolution(run, resolution, { reason: getUnavailableReason(code), dealerId });
}

/**
 * Tenant config of THIS brand and market, then the market check, the whitelist, and finally the
 * deferred route loaders: nothing renders before the loaders have re-run with the gate open.
 */
async function openGate(run: IGateRun, dealerId: string): Promise<void> {
  const { brand, market } = run;
  // Read before anything is marked: on a remount for the same brand, market and dealer the route loaders already
  // ran with the gate open, so re-running them (initSession, session:resume) would only repeat their side effects.
  const loadersAlreadyRan = isDealerGatePassed(brand, market);

  const inactive = await activateDealerTenant(brand, market);
  if (run.isCancelled()) {
    return;
  }

  if (inactive) {
    if (dealerId) {
      await recoverFromGateFailure(run, dealerId, EErrorCode.DISABLED_MARKET);
    } else {
      run.fail(EErrorCode.DISABLED_MARKET);
    }
    return;
  }

  if (dealerId) {
    const access = await run.validateAccess(brand, market, dealerId);
    if (run.isCancelled()) {
      return;
    }

    if (!access.allowed) {
      await recoverFromGateFailure(run, dealerId, access.code ?? EErrorCode.DEALER_NOT_WHITELISTED);
      return;
    }
  }

  markDealerGatePassed(brand, market, dealerId);
  if (dealerId) {
    rememberConfirmedDealerId(dealerId);
  }
  if (!loadersAlreadyRan) {
    await run.revalidate();
  }
  run.show(OPEN);
}

async function resolveDealer(run: IGateRun): Promise<void> {
  const { brand, market, sessionId, dealerIds } = run;

  // Read and removed in one step: a parameter left by a cancelled run belongs to the brand and market being left.
  const urlDealerId = consumeDealerIdFromUrl();
  const storedDealerId = run.readStoredDealerId() || undefined;
  // The session's own dealer is only a hint, so it is read only when nothing more specific was given.
  const needsHint = !urlDealerId && !storedDealerId && sessionId;
  const sessionDealerId = needsHint ? await readSessionDealerHint(sessionId) : undefined;
  if (run.isCancelled()) {
    return;
  }

  const resolution = resolveDealerForLogin({
    dealerIds,
    brand,
    market,
    urlDealerId,
    storedDealerId,
    sessionDealerId,
    unavailable: readUnavailableDealerIds(),
    lastConfirmedDealerId: readLastConfirmedDealerId(),
  });

  // An account with no Dealer ID at all (40207) is reachable: it stops here.
  if (resolution.status === 'none') {
    run.fail(EErrorCode.DEALER_NO_DEALER_IDS);
    return;
  }

  if (resolution.status === 'choose') {
    showDialog(run, resolution.options, resolution.error, resolution.preselectedDealerId);
    return;
  }

  await commitAndOpen(run, resolution.dealerId);
}

/** Runs the gate once: a dealer resolves a Dealer ID first, anyone else opens it with an empty one. */
export function runDealerGate(run: IGateRun): Promise<void> {
  return guarded(run, () => (run.isDealer ? resolveDealer(run) : openGate(run, '')));
}
